import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import API_BASE_URL from "../Config/config";
import { isCollegeChallengeEnabled } from "../utils/collegeChallengeAccess";
import CollegeHeader from "./CollegeHeader";
import CollegeHomeSideBar from "./CollegeHomeSideBar";
import CollegeFooter from "./CollegeFooter";
import EnterpriseSalesContactPage, { isSalesPartnerCollege } from "./SalesTeamManagedPage";
import {
  CreditCard,
  Award,
  Check,
  AlertCircle,
  TrendingUp,
  Shield,
  RefreshCw,
  ChevronRight,
  Tag,
  X,
  Loader,
  Building2,
  FileText,
  Banknote,
  Clock,
} from "lucide-react";

// --- Helpers -----------------------------------------------------------------

const formatINR = (amount) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(amount);

const RAZORPAY_MAX_INR = parseInt(process.env.REACT_APP_RAZORPAY_MAX_INR || "5000000", 10);
const ENTERPRISE_THRESHOLD_INR = parseInt(process.env.REACT_APP_ENTERPRISE_THRESHOLD_INR || "500000", 10);

// Compact feature highlights for Titanium Elite (8 max, 2-3 words each)
const PLAN_FEATURES = [
  "AI Learning",
  "Analytics Dashboard",
  "Bulk Enrollment",
  "GitHub Integration",
  "Certificates",
  "LMS Integration",
  "Priority Support",
  "Enterprise Security",
];

// --- StatTile ----------------------------------------------------------------

const StatTile = ({ label, value, accent, small }) => (
  <div style={{
    flex: 1, minWidth: "110px",
    padding: "14px 16px",
    borderRadius: "12px",
    background: "#F8FAFC",
    border: "1.5px solid #F1F5F9",
    display: "flex", flexDirection: "column", gap: "3px",
  }}>
    <span style={{ fontSize: "10px", fontWeight: 700, color: "#94A3B8", textTransform: "uppercase", letterSpacing: "0.06em" }}>
      {label}
    </span>
    <span style={{
      fontSize: small ? "17px" : "24px",
      fontWeight: 900,
      color: accent || "#0F172A",
      lineHeight: 1.1,
      letterSpacing: "-0.02em",
    }}>
      {value}
    </span>
  </div>
);

// --- CollegeCreditPurchase (main page) ---------------------------------------

const CollegeCreditPurchase = () => {
  const navigate = useNavigate();

  // College Challenge eligibility — read once at render time from localStorage
  const collegeChallengeEnabled = isCollegeChallengeEnabled();

  const [plans, setPlans] = useState([]);
  const [currentBalance, setCurrentBalance] = useState(null);
  const [collegeInfo, setCollegeInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [quantity, setQuantity] = useState("");
  const [quantityError, setQuantityError] = useState("");
  const [purchasing, setPurchasing] = useState(false);
  const [enterpriseLoading, setEnterpriseLoading] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [lastCreditAdded, setLastCreditAdded] = useState(null);
  const [lastOrderId, setLastOrderId] = useState(null);

  const [couponInput, setCouponInput] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponError, setCouponError] = useState("");

  const getUser = () => {
    try { return JSON.parse(localStorage.getItem("user")); } catch { return null; }
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const user = getUser();
      if (!user) { setError("Please log in to view plans."); return; }
      if (user.role === "student" || user.roll_number) {
        setError("This page is for college administrators only.");
        return;
      }
      const college_id = user?.id;
      if (!college_id) { setError("Please log in to view plans."); return; }

      const [plansRes, balanceRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/credits/plans?college_id=${college_id}`),
        axios.post(`${API_BASE_URL}/credits/balance`, { college_id }),
      ]);

      if (plansRes.data.success) {
        setPlans(plansRes.data.plans);
        setCollegeInfo(plansRes.data.college);
      }
      if (balanceRes.data.success) {
        setCurrentBalance(balanceRes.data.credits);
      }
    } catch (err) {
      console.error("[CollegeCreditPurchase] fetch error:", err);
      setError("Failed to load plan data. Please refresh the page.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Sales Partner check is handled inline in the render path (after loading)
  // to avoid any flicker of pricing UI before the redirect fires.

  // Resolve the Titanium Elite plan from the API response
  const titaniumPlan = plans.find((p) => p.plan_code === "TITANIUM_ELITE") || null;

  // Initialise quantity to plan minimum when plan loads
  useEffect(() => {
    if (titaniumPlan && quantity === "") {
      setQuantity(String(titaniumPlan.min_credits));
    }
  }, [titaniumPlan]);

  const handleQuantityChange = (val) => {
    setQuantity(val);
    if (!titaniumPlan) return;
    const n = parseInt(val, 10);
    if (!val || isNaN(n) || n <= 0) {
      setQuantityError("Enter a valid number of credits.");
    } else if (n < titaniumPlan.min_credits) {
      setQuantityError(`Minimum ${titaniumPlan.min_credits.toLocaleString("en-IN")} credits required.`);
    } else if (n > titaniumPlan.max_credits) {
      setQuantityError(`Maximum ${titaniumPlan.max_credits.toLocaleString("en-IN")} credits for your institution.`);
    } else {
      setQuantityError("");
    }
  };

  const handleEnterprisePurchase = async () => {
    if (!titaniumPlan) return;
    const n = parseInt(quantity, 10);
    if (!quantity || isNaN(n) || n <= 0) { setQuantityError("Enter a valid number of credits."); return; }
    if (n < titaniumPlan.min_credits) { setQuantityError(`Minimum ${titaniumPlan.min_credits.toLocaleString("en-IN")} credits required.`); return; }
    if (n > titaniumPlan.max_credits) { setQuantityError(`Maximum ${titaniumPlan.max_credits.toLocaleString("en-IN")} credits for your institution.`); return; }

    setEnterpriseLoading(true);
    try {
      const user = getUser();
      if (!user || user.role === "student" || user.roll_number) {
        alert("Please log in as a college administrator to generate a quotation.");
        return;
      }
      const college_id = user?.id;
      const purchased_by = user?.username || user?.name;
      if (!college_id) { alert("Please log in as a college administrator."); return; }

      const res = await axios.post(`${API_BASE_URL}/credits/enterprise/quotation`, {
        college_id,
        plan_code: titaniumPlan.plan_code,
        quantity: n,
        purchased_by,
      });

      if (!res.data.success) {
        alert(res.data.error || "Failed to generate quotation. Please try again.");
        return;
      }

      const quotation = res.data.quotation;

      axios.post(`${API_BASE_URL}/credits/enterprise/quotation/downloaded`, {
        quotation_number: quotation.quotationNumber,
        college_id,
      }).catch(() => {});

      navigate(`/college/enterprise-purchase/${quotation.enterprisePurchaseId}`, {
        state: { quotation },
      });
    } catch (err) {
      console.error("[handleEnterprisePurchase] Error:", err);
      alert(err.response?.data?.error || "Failed to generate quotation. Please try again.");
    } finally {
      setEnterpriseLoading(false);
    }
  };

  const handleApplyCoupon = async () => {
    const code = couponInput.trim().toUpperCase();
    if (!code) { setCouponError("Enter a coupon code."); return; }
    const user = getUser();
    if (!user?.id) { setCouponError("Please log in first."); return; }
    setCouponLoading(true);
    setCouponError("");
    try {
      const res = await axios.post(`${API_BASE_URL}/api/college-challenge/validate-coupon`, {
        couponCode: code,
        collegeId: user.id,
      });
      if (res.data.valid) {
        setAppliedCoupon({ code, discount_percentage: res.data.discount_percentage });
        setCouponInput("");
      } else {
        setCouponError("Coupon is not valid.");
      }
    } catch (err) {
      setCouponError(err.response?.data?.error || "Invalid or expired coupon code.");
    } finally {
      setCouponLoading(false);
    }
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponInput("");
    setCouponError("");
  };

  const handlePurchase = async () => {
    if (!titaniumPlan) return;
    const n = parseInt(quantity, 10);
    if (!quantity || isNaN(n) || n <= 0) { setQuantityError("Enter a valid number of credits."); return; }
    if (n < titaniumPlan.min_credits) { setQuantityError(`Minimum ${titaniumPlan.min_credits.toLocaleString("en-IN")} credits required.`); return; }
    if (n > titaniumPlan.max_credits) { setQuantityError(`Maximum ${titaniumPlan.max_credits.toLocaleString("en-IN")} credits for your institution.`); return; }

    const pricePerCredit = titaniumPlan.effective_price_per_credit;
    const discountedPricePerCredit = appliedCoupon?.discount_percentage > 0
      ? Math.round(pricePerCredit * (1 - appliedCoupon.discount_percentage / 100))
      : pricePerCredit;
    const totalInr = n * discountedPricePerCredit;
    const totalWithGstInr = Math.round(totalInr * 1.18);
    if (totalWithGstInr > ENTERPRISE_THRESHOLD_INR) {
      setQuantityError("This amount requires Enterprise Purchase. Click 'Generate Quotation' below.");
      return;
    }

    setPurchasing(true);
    try {
      const user = getUser();
      if (!user || user.role === "student" || user.roll_number) {
        alert("Please log in as a college administrator to purchase credits.");
        return;
      }
      const college_id = user?.id;
      const purchased_by = user?.username || user?.name;
      if (!college_id) { alert("Please log in as a college administrator to purchase credits."); return; }

      const orderRes = await axios.post(`${API_BASE_URL}/credits/purchase/create-order`, {
        college_id,
        plan_code: titaniumPlan.plan_code,
        quantity: n,
        purchased_by,
        ...(appliedCoupon ? { coupon_code: appliedCoupon.code } : {}),
      });

      if (!orderRes.data.success) { alert(orderRes.data.error || "Failed to create order. Please try again."); return; }

      const { order, key } = orderRes.data;
      const rzpOptions = {
        key,
        amount: order.amount,
        currency: order.currency,
        name: "The Skill Genie",
        description: `${titaniumPlan.plan_name} — ${n.toLocaleString("en-IN")} Credits`,
        order_id: order.order_id,
        handler: async (response) => {
          try {
            const verifyRes = await axios.post(`${API_BASE_URL}/credits/purchase/verify`, {
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_signature: response.razorpay_signature,
            });
            if (verifyRes.data.success) {
              setLastOrderId(verifyRes.data.order_id || order.order_id);
              setLastCreditAdded(verifyRes.data.credits_added);
              setPaymentSuccess(true);
              setQuantity("");
              fetchData();
            } else {
              alert("Payment could not be verified. Contact support with your payment ID.");
            }
          } catch (verifyErr) {
            console.error("[handlePurchase] verify error:", verifyErr);
            alert("Payment completed but verification failed. Contact support with payment ID: " + response.razorpay_payment_id);
          }
        },
        prefill: { name: user?.name || "", email: user?.email || "" },
        theme: { color: "#6D4AFF" },
        modal: { ondismiss: () => setPurchasing(false) },
      };

      const rzp = new window.Razorpay(rzpOptions);
      rzp.on("payment.failed", (resp) => {
        console.error("[Razorpay] Payment failed:", resp.error);
        alert(`Payment failed: ${resp.error.description || "Unknown error"}`);
        setPurchasing(false);
      });
      rzp.open();
    } catch (err) {
      console.error("[handlePurchase] Error:", err);
      alert(err.response?.data?.error || "Failed to initiate purchase. Please try again.");
    } finally {
      setPurchasing(false);
    }
  };

  // ── Loading ──────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{ minHeight: "100vh", background: "#F8FAFC", display: "flex", flexDirection: "column" }}>
        <CollegeHeader />
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ textAlign: "center" }}>
            <RefreshCw style={{ width: "32px", height: "32px", color: "#6D4AFF", margin: "0 auto 16px", animation: "spin 1s linear infinite" }} />
            <p style={{ fontSize: "14px", color: "#64748B", fontWeight: 500 }}>Loading plan details...</p>
          </div>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // ── Error ────────────────────────────────────────────────────────────────────
  if (error) {
    return (
      <div style={{ minHeight: "100vh", background: "#F8FAFC", display: "flex", flexDirection: "column" }}>
        <CollegeHeader />
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: "24px" }}>
          <div style={{ textAlign: "center", maxWidth: "400px" }}>
            <div style={{ width: "64px", height: "64px", borderRadius: "18px", background: "#FEF2F2", border: "1px solid #FECACA", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" }}>
              <AlertCircle style={{ width: "30px", height: "30px", color: "#DC2626" }} />
            </div>
            <p style={{ fontSize: "18px", fontWeight: 800, color: "#0F172A", marginBottom: "8px" }}>Something went wrong</p>
            <p style={{ fontSize: "14px", color: "#64748B", marginBottom: "24px", lineHeight: 1.6 }}>{error}</p>
            <button
              onClick={fetchData}
              style={{
                padding: "12px 28px", borderRadius: "12px", border: "none",
                background: "linear-gradient(135deg, #6D4AFF, #4C1D95)", color: "#FFFFFF",
                fontSize: "14px", fontWeight: 700, cursor: "pointer",
              }}
            >
              Try Again
            </button>
          </div>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // ── Sales Partner gate ───────────────────────────────────────────────────────
  // Evaluated after loading completes so the pricing UI is never rendered, even
  // for a single frame. The API now returns lead_ambassador_id in the college object.
  if (isSalesPartnerCollege(collegeInfo?.lead_ambassador_id)) {
    return <EnterpriseSalesContactPage />;
  }

  // ── Derived values ────────────────────────────────────────────────────────────
  const activePlanName = currentBalance?.active_plan_code
    ? plans.find((p) => p.plan_code === currentBalance.active_plan_code)?.plan_name || currentBalance.active_plan_code
    : null;

  const planDaysLeft = currentBalance?.plan_valid_till
    ? Math.ceil((new Date(currentBalance.plan_valid_till) - Date.now()) / (1000 * 60 * 60 * 24))
    : null;

  const discountPercent = appliedCoupon?.discount_percentage || 0;
  const qty = parseInt(quantity, 10);
  const discountedPricePerCredit = titaniumPlan
    ? (discountPercent > 0
        ? Math.round(titaniumPlan.effective_price_per_credit * (1 - discountPercent / 100))
        : titaniumPlan.effective_price_per_credit)
    : 0;
  const total = titaniumPlan && !isNaN(qty) && qty > 0 ? qty * discountedPricePerCredit : 0;
  const originalTotal = titaniumPlan && !isNaN(qty) && qty > 0 ? qty * titaniumPlan.effective_price_per_credit : 0;
  const gstAmount = Math.round(total * 0.18);
  const totalWithGst = total + gstAmount;
  const isEnterprise = totalWithGst > ENTERPRISE_THRESHOLD_INR;
  const isQuantityValid = titaniumPlan && !isNaN(qty) && qty >= titaniumPlan.min_credits && !quantityError;

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div className="college-main-layout" style={{ background: "#F8FAFC", fontFamily: "'Inter', 'Segoe UI', sans-serif" }}>
      <CollegeHeader />
      <div className="college-content-wrapper">
        <CollegeHomeSideBar />
        <main className="college-main-content">
          <div style={{ maxWidth: "1200px", margin: "0 auto", padding: "36px 24px 80px" }}>

            {/* ── PAGE HEADER ─────────────────────────────────────────────────── */}
            <div style={{ marginBottom: "28px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <div style={{
                  width: "38px", height: "38px", borderRadius: "11px",
                  background: "rgba(109,74,255,0.10)",
                  display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                }}>
                  <CreditCard style={{ width: "18px", height: "18px", color: "#6D4AFF" }} />
                </div>
                <div>
                  <h1 style={{ fontSize: "22px", fontWeight: 800, color: "#0F172A", letterSpacing: "-0.02em", margin: 0 }}>
                    Purchase Credits
                  </h1>
                  <p style={{ fontSize: "13px", color: "#94A3B8", margin: 0, fontWeight: 500 }}>
                    Titanium Elite · Enterprise plan for your institution
                  </p>
                </div>
              </div>
            </div>

            {/* ── PAYMENT SUCCESS BANNER ──────────────────────────────────────── */}
            {paymentSuccess && (
              <div style={{
                display: "flex", alignItems: "center", gap: "16px",
                padding: "18px 22px", borderRadius: "16px",
                background: "#F0FDF4", border: "1.5px solid #BBF7D0",
                marginBottom: "24px",
              }}>
                <div style={{
                  width: "40px", height: "40px", borderRadius: "11px",
                  background: "#DCFCE7", border: "1px solid #BBF7D0",
                  display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                }}>
                  <Check style={{ width: "20px", height: "20px", color: "#16A34A" }} />
                </div>
                <div style={{ flex: 1 }}>
                  <p style={{ fontSize: "14px", fontWeight: 700, color: "#14532D", margin: "0 0 2px" }}>
                    Payment Successful
                  </p>
                  <p style={{ fontSize: "13px", color: "#15803D", margin: 0, fontWeight: 500 }}>
                    {lastCreditAdded
                      ? `${lastCreditAdded.toLocaleString("en-IN")} credits have been added to your institution's wallet.`
                      : "Credits have been added to your wallet."}
                  </p>
                </div>
                <button
                  onClick={() => setPaymentSuccess(false)}
                  style={{ background: "none", border: "none", cursor: "pointer", fontSize: "18px", color: "#86EFAC", lineHeight: 1 }}
                >×</button>
              </div>
            )}

            {/* ── CREDIT SUMMARY CARD ─────────────────────────────────────────── */}
            {currentBalance && (
              <div style={{
                background: "#FFFFFF",
                border: "1.5px solid #E2E8F0",
                borderRadius: "20px",
                boxShadow: "0 4px 20px rgba(15,23,42,0.06)",
                marginBottom: "28px",
                overflow: "hidden",
              }}>
                <div style={{ padding: "24px 28px" }}>
                  <div style={{
                    display: "flex", flexWrap: "wrap",
                    alignItems: "center", justifyContent: "space-between",
                    gap: "12px", marginBottom: "20px",
                  }}>
                    <div>
                      <p style={{ fontSize: "11px", fontWeight: 700, color: "#94A3B8", textTransform: "uppercase", letterSpacing: "0.08em", margin: "0 0 4px" }}>
                        Billing Summary
                      </p>
                      <p style={{ fontSize: "13px", color: "#475569", margin: 0, fontWeight: 500 }}>
                        {collegeInfo?.name || "Your Institution"}
                      </p>
                    </div>

                    <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                      {activePlanName && (
                        <div style={{
                          display: "inline-flex", alignItems: "center", gap: "7px",
                          background: "rgba(109,74,255,0.08)", border: "1.5px solid rgba(109,74,255,0.25)",
                          borderRadius: "99px", padding: "6px 14px",
                        }}>
                          <span style={{ width: "8px", height: "8px", borderRadius: "50%", background: "#6D4AFF", flexShrink: 0 }} />
                          <span style={{ fontSize: "12px", fontWeight: 700, color: "#4C1D95" }}>
                            Active: {activePlanName}
                          </span>
                        </div>
                      )}

                      {currentBalance.plan_valid_till && planDaysLeft !== null && planDaysLeft > 0 && (
                        <div style={{
                          display: "inline-flex", alignItems: "center", gap: "6px",
                          background: planDaysLeft <= 30 ? "#FFFBEB" : "#F8FAFC",
                          border: `1px solid ${planDaysLeft <= 30 ? "#FDE68A" : "#E2E8F0"}`,
                          borderRadius: "99px", padding: "6px 12px",
                        }}>
                          <span style={{ fontSize: "12px", fontWeight: 600, color: planDaysLeft <= 30 ? "#B45309" : "#64748B" }}>
                            Valid till {new Date(currentBalance.plan_valid_till).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                          </span>
                        </div>
                      )}

                      {currentBalance.is_premium_partner && (
                        <div style={{
                          display: "inline-flex", alignItems: "center", gap: "6px",
                          background: "#FFFBEB", border: "1px solid #FDE68A",
                          borderRadius: "99px", padding: "6px 12px",
                        }}>
                          <Award style={{ width: "13px", height: "13px", color: "#D4A017" }} />
                          <span style={{ fontSize: "12px", fontWeight: 700, color: "#92400E" }}>
                            Premium Partner #{currentBalance.premium_partner_number}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
                    <StatTile
                      label="Available Credits"
                      value={currentBalance.available.toLocaleString("en-IN")}
                      accent={currentBalance.available === 0 ? "#DC2626" : "#6D4AFF"}
                    />
                    <StatTile label="Purchased" value={currentBalance.total_purchased.toLocaleString("en-IN")} small />
                    <StatTile label="Used" value={currentBalance.total_used.toLocaleString("en-IN")} accent="#64748B" small />
                    <StatTile
                      label="Remaining"
                      value={(currentBalance.total_purchased - currentBalance.total_used).toLocaleString("en-IN")}
                      accent="#059669"
                      small
                    />
                  </div>

                  {currentBalance.total_purchased > 0 && (
                    <div style={{ marginTop: "18px" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px" }}>
                        <span style={{ fontSize: "11px", fontWeight: 600, color: "#94A3B8", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                          Credit Utilisation
                        </span>
                        <span style={{ fontSize: "11px", fontWeight: 700, color: "#475569" }}>
                          {Math.round((currentBalance.total_used / currentBalance.total_purchased) * 100)}%
                        </span>
                      </div>
                      <div style={{ height: "6px", borderRadius: "99px", background: "#F1F5F9", overflow: "hidden" }}>
                        <div style={{
                          height: "100%",
                          width: `${Math.min(100, (currentBalance.total_used / currentBalance.total_purchased) * 100)}%`,
                          borderRadius: "99px",
                          background: currentBalance.available === 0 ? "#EF4444" : "linear-gradient(90deg, #6D4AFF, #9B72FF)",
                          transition: "width 700ms cubic-bezier(.4,0,.2,1)",
                        }} />
                      </div>
                    </div>
                  )}
                </div>

                {/* Alerts */}
                {(() => {
                  const alerts = [];
                  if (planDaysLeft !== null && planDaysLeft <= 0) {
                    alerts.push(
                      <div key="expired" style={{ display: "flex", alignItems: "flex-start", gap: "12px", padding: "14px 18px", background: "#FEF2F2", borderTop: "1px solid #FECACA" }}>
                        <AlertCircle style={{ width: "16px", height: "16px", color: "#DC2626", flexShrink: 0, marginTop: "1px" }} />
                        <div>
                          <p style={{ fontSize: "13px", fontWeight: 700, color: "#7F1D1D", margin: "0 0 2px" }}>Subscription Expired</p>
                          <p style={{ fontSize: "12px", color: "#991B1B", margin: 0, fontWeight: 500 }}>
                            Your plan expired on {new Date(currentBalance.plan_valid_till).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}. Renew your Titanium Elite subscription below.
                          </p>
                        </div>
                      </div>
                    );
                  } else if (planDaysLeft !== null && planDaysLeft <= 30) {
                    alerts.push(
                      <div key="expiring" style={{ display: "flex", alignItems: "flex-start", gap: "12px", padding: "14px 18px", background: "#FFFBEB", borderTop: "1px solid #FDE68A" }}>
                        <AlertCircle style={{ width: "16px", height: "16px", color: "#D97706", flexShrink: 0, marginTop: "1px" }} />
                        <div>
                          <p style={{ fontSize: "13px", fontWeight: 700, color: "#92400E", margin: "0 0 2px" }}>Subscription Expiring Soon</p>
                          <p style={{ fontSize: "12px", color: "#B45309", margin: 0, fontWeight: 500 }}>
                            Expires in {planDaysLeft} day{planDaysLeft !== 1 ? "s" : ""}. Renew early to avoid service interruption.
                          </p>
                        </div>
                      </div>
                    );
                  }
                  if (currentBalance.available === 0) {
                    alerts.push(
                      <div key="exhausted" style={{ display: "flex", alignItems: "flex-start", gap: "12px", padding: "14px 18px", background: "#FEF2F2", borderTop: "1px solid #FECACA" }}>
                        <AlertCircle style={{ width: "16px", height: "16px", color: "#DC2626", flexShrink: 0, marginTop: "1px" }} />
                        <div>
                          <p style={{ fontSize: "13px", fontWeight: 700, color: "#7F1D1D", margin: "0 0 2px" }}>Credits Exhausted</p>
                          <p style={{ fontSize: "12px", color: "#991B1B", margin: 0, fontWeight: 500 }}>
                            Purchase credits below to continue enrolling students.
                          </p>
                        </div>
                      </div>
                    );
                  } else if (currentBalance.low_credit_warning) {
                    alerts.push(
                      <div key="low" style={{ display: "flex", alignItems: "flex-start", gap: "12px", padding: "14px 18px", background: "#FFF7ED", borderTop: "1px solid #FED7AA" }}>
                        <AlertCircle style={{ width: "16px", height: "16px", color: "#EA580C", flexShrink: 0, marginTop: "1px" }} />
                        <div>
                          <p style={{ fontSize: "13px", fontWeight: 700, color: "#7C2D12", margin: "0 0 2px" }}>Low Credit Balance</p>
                          <p style={{ fontSize: "12px", color: "#9A3412", margin: 0, fontWeight: 500 }}>
                            Balance below 10% of total purchased. Top up to avoid enrollment disruptions.
                          </p>
                        </div>
                      </div>
                    );
                  }
                  return alerts.length > 0 ? <div>{alerts}</div> : null;
                })()}
              </div>
            )}

            {/* ── SCHOLARSHIP COUPON (visible only to College Challenge eligible colleges) ── */}
            {collegeChallengeEnabled && <div style={{
              background: "#FFFFFF",
              border: appliedCoupon ? "1.5px solid #BBF7D0" : "1.5px solid #E2E8F0",
              borderRadius: "16px",
              padding: "20px 24px",
              marginBottom: "32px",
              boxShadow: "0 2px 10px rgba(15,23,42,0.05)",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "14px" }}>
                <div style={{
                  width: "34px", height: "34px", borderRadius: "10px",
                  background: appliedCoupon ? "rgba(5,150,105,0.10)" : "rgba(109,74,255,0.08)",
                  display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                }}>
                  <Tag style={{ width: "16px", height: "16px", color: appliedCoupon ? "#059669" : "#6D4AFF" }} />
                </div>
                <div>
                  <p style={{ fontSize: "13px", fontWeight: 700, color: "#0F172A", margin: 0 }}>Scholarship Coupon</p>
                  <p style={{ fontSize: "11px", color: "#94A3B8", margin: 0, fontWeight: 500 }}>Apply your College Challenge coupon code for a discount</p>
                </div>
              </div>

              {appliedCoupon ? (
                <div style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  background: "#F0FDF4", border: "1px solid #BBF7D0",
                  borderRadius: "12px", padding: "12px 16px",
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <Check style={{ width: "16px", height: "16px", color: "#16A34A", strokeWidth: 3 }} />
                    <div>
                      <p style={{ fontSize: "13px", fontWeight: 700, color: "#14532D", margin: 0 }}>
                        {appliedCoupon.code}
                        <span style={{
                          marginLeft: "8px", fontSize: "11px", fontWeight: 700,
                          color: "#065F46", background: "#D1FAE5",
                          border: "1px solid #A7F3D0", borderRadius: "99px",
                          padding: "2px 8px",
                        }}>
                          {appliedCoupon.discount_percentage}% OFF
                        </span>
                      </p>
                      <p style={{ fontSize: "11px", color: "#15803D", margin: "2px 0 0", fontWeight: 500 }}>
                        Scholarship discount applied to plan pricing
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={handleRemoveCoupon}
                    style={{ background: "none", border: "none", cursor: "pointer", color: "#86EFAC", padding: "4px", display: "flex", alignItems: "center" }}
                    title="Remove coupon"
                  >
                    <X style={{ width: "16px", height: "16px" }} />
                  </button>
                </div>
              ) : (
                <div>
                  <div style={{ display: "flex", gap: "8px" }}>
                    <input
                      type="text"
                      value={couponInput}
                      onChange={(e) => { setCouponInput(e.target.value.toUpperCase()); setCouponError(""); }}
                      onKeyDown={(e) => e.key === "Enter" && handleApplyCoupon()}
                      placeholder="e.g. TSGCOL50-ABC123"
                      style={{
                        flex: 1, padding: "10px 14px",
                        borderRadius: "10px",
                        border: couponError ? "1.5px solid #FCA5A5" : "1.5px solid #E2E8F0",
                        background: "#F8FAFC",
                        fontSize: "13px", fontWeight: 600,
                        color: "#0F172A", outline: "none",
                        letterSpacing: "0.04em",
                        fontFamily: "monospace",
                      }}
                    />
                    <button
                      onClick={handleApplyCoupon}
                      disabled={couponLoading}
                      style={{
                        padding: "10px 20px", borderRadius: "10px", border: "none",
                        background: couponLoading ? "#94A3B8" : "linear-gradient(135deg, #6D4AFF, #4C1D95)",
                        color: "#FFFFFF", fontSize: "13px", fontWeight: 700,
                        cursor: couponLoading ? "not-allowed" : "pointer",
                        display: "flex", alignItems: "center", gap: "6px",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {couponLoading
                        ? <><Loader style={{ width: "13px", height: "13px", animation: "spin 1s linear infinite" }} /> Checking...</>
                        : "Apply"}
                    </button>
                  </div>
                  {couponError && (
                    <div style={{ display: "flex", alignItems: "center", gap: "6px", marginTop: "8px" }}>
                      <AlertCircle style={{ width: "13px", height: "13px", color: "#EF4444", flexShrink: 0 }} />
                      <span style={{ fontSize: "12px", color: "#DC2626", fontWeight: 500 }}>{couponError}</span>
                    </div>
                  )}
                </div>
              )}
            </div>}

            {/* ── MAIN 65 / 35 LAYOUT ─────────────────────────────────────────── */}
            {titaniumPlan ? (
              <div className="credit-purchase-grid" style={{
                display: "grid",
                gridTemplateColumns: "65fr 35fr",
                gap: "28px",
                alignItems: "stretch",
              }}>

                {/* ── LEFT COLUMN: Plan Details ─────────────────────────────── */}
                <div style={{
                  background: "#FFFFFF",
                  borderRadius: "24px",
                  border: "1.5px solid #E2E8F0",
                  boxShadow: "0 6px 32px rgba(109,74,255,0.07)",
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                }}>
                  {/* Gold accent top strip */}
                  <div style={{ height: "5px", background: "linear-gradient(90deg, #D4A017, #F5C842, #B8860B)" }} />

                  <div style={{ padding: "28px 28px 24px", flex: 1 }}>
                    {/* Plan identity */}
                    <div style={{ display: "flex", alignItems: "center", gap: "16px", marginBottom: "24px" }}>
                      <div style={{
                        width: "52px", height: "52px", borderRadius: "16px", flexShrink: 0,
                        background: "linear-gradient(135deg, rgba(212,160,23,0.15), rgba(212,160,23,0.06))",
                        border: "1.5px solid rgba(212,160,23,0.3)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                      }}>
                        <Shield style={{ width: "24px", height: "24px", color: "#D4A017" }} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap", marginBottom: "4px" }}>
                          <h2 style={{ fontSize: "20px", fontWeight: 800, color: "#0F172A", margin: 0, letterSpacing: "-0.02em" }}>
                            {titaniumPlan.plan_name}
                          </h2>
                          <span style={{
                            fontSize: "10px", fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase",
                            background: "linear-gradient(135deg, #D4A017, #B8860B)",
                            color: "#FFFFFF", borderRadius: "99px", padding: "3px 9px",
                          }}>
                            Enterprise
                          </span>
                        </div>
                        <p style={{ fontSize: "13px", color: "#64748B", margin: 0, fontWeight: 500 }}>
                          Enterprise-grade platform for large institutions at scale.
                        </p>
                      </div>
                    </div>

                    {/* Key specs row */}
                    <div style={{
                      display: "grid", gridTemplateColumns: "1fr 1fr 1fr",
                      gap: "10px", marginBottom: "20px",
                    }}>
                      {[
                        {
                          label: "Credits",
                          value: `${titaniumPlan.min_credits.toLocaleString("en-IN")}+`,
                          color: "#6D4AFF",
                        },
                        {
                          label: "Per Student",
                          value: formatINR(discountPercent > 0 ? discountedPricePerCredit : titaniumPlan.effective_price_per_credit),
                          sub: discountPercent > 0 ? `${discountPercent}% off` : null,
                          color: discountPercent > 0 ? "#059669" : "#D4A017",
                        },
                        {
                          label: "Validity",
                          value: "1 Year",
                          color: "#6D4AFF",
                        },
                      ].map((spec, i) => (
                        <div key={i} style={{
                          background: "#F8FAFC",
                          border: "1.5px solid #F1F5F9",
                          borderRadius: "12px",
                          padding: "12px 14px",
                        }}>
                          <p style={{ fontSize: "10px", fontWeight: 700, color: "#94A3B8", textTransform: "uppercase", letterSpacing: "0.07em", margin: "0 0 4px" }}>
                            {spec.label}
                          </p>
                          <p style={{ fontSize: "15px", fontWeight: 900, color: spec.color, margin: 0, letterSpacing: "-0.01em" }}>
                            {spec.value}
                          </p>
                          {spec.sub && (
                            <p style={{ fontSize: "10px", color: "#059669", margin: "2px 0 0", fontWeight: 600 }}>
                              {spec.sub}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>

                    {/* Subscription validity pill */}
                    <div style={{
                      display: "inline-flex", alignItems: "center", gap: "8px",
                      background: "rgba(109,74,255,0.06)",
                      border: "1px solid rgba(109,74,255,0.15)",
                      borderRadius: "99px", padding: "6px 14px",
                      marginBottom: "20px",
                    }}>
                      <Clock style={{ width: "13px", height: "13px", color: "#6D4AFF", flexShrink: 0 }} />
                      <span style={{ fontSize: "12px", fontWeight: 700, color: "#4C1D95" }}>
                        1 Year Subscription
                      </span>
                      <span style={{ fontSize: "11px", color: "#94A3B8", fontWeight: 500 }}>· 365 Days Platform Access</span>
                    </div>

                    {/* Divider */}
                    <div style={{ height: "1px", background: "#F1F5F9", marginBottom: "20px" }} />

                    {/* Compact feature grid */}
                    <p style={{ fontSize: "10px", fontWeight: 700, color: "#94A3B8", textTransform: "uppercase", letterSpacing: "0.08em", margin: "0 0 12px" }}>
                      What's Included
                    </p>
                    <div style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "8px",
                    }}>
                      {PLAN_FEATURES.map((feat, i) => (
                        <div key={i} style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          <div style={{
                            width: "18px", height: "18px", borderRadius: "6px", flexShrink: 0,
                            background: "rgba(109,74,255,0.08)",
                            display: "flex", alignItems: "center", justifyContent: "center",
                          }}>
                            <Check style={{ width: "10px", height: "10px", color: "#6D4AFF", strokeWidth: 3 }} />
                          </div>
                          <span style={{ fontSize: "12px", color: "#475569", fontWeight: 600 }}>
                            {feat}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* ── RIGHT COLUMN: Purchase Panel ──────────────────────────── */}
                <div style={{ position: "sticky", top: "24px", display: "flex", flexDirection: "column" }}>
                  <div style={{
                    background: "#FFFFFF",
                    borderRadius: "20px",
                    border: "1.5px solid #E2E8F0",
                    boxShadow: "0 8px 40px rgba(109,74,255,0.10)",
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                  }}>
                    {/* Gold top accent */}
                    <div style={{ height: "4px", background: "linear-gradient(90deg, #6D4AFF, #9B72FF, #D4A017)" }} />

                    <div style={{ padding: "24px", flex: 1, display: "flex", flexDirection: "column" }}>
                      {/* Panel header */}
                      <div style={{ marginBottom: "18px" }}>
                        <p style={{ fontSize: "10px", fontWeight: 700, color: "#94A3B8", textTransform: "uppercase", letterSpacing: "0.08em", margin: "0 0 3px" }}>
                          Configure Your Order
                        </p>
                        <p style={{ fontSize: "15px", fontWeight: 800, color: "#0F172A", margin: 0, letterSpacing: "-0.01em" }}>
                          {titaniumPlan.plan_name}
                        </p>
                      </div>

                      {/* Credit quantity */}
                      <div style={{ marginBottom: "18px" }}>
                        <label style={{
                          display: "flex", justifyContent: "space-between", alignItems: "center",
                          fontSize: "11px", fontWeight: 700, color: "#475569",
                          textTransform: "uppercase", letterSpacing: "0.06em",
                          marginBottom: "8px",
                        }}>
                          <span>Number of Credits</span>
                          <span style={{ fontSize: "10px", color: "#94A3B8", fontWeight: 500, textTransform: "none", letterSpacing: 0 }}>
                            Min {titaniumPlan.min_credits.toLocaleString("en-IN")}
                          </span>
                        </label>

                        {/* Stepper */}
                        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                          <button
                            onClick={() => handleQuantityChange(String(Math.max(titaniumPlan.min_credits, (isNaN(qty) ? titaniumPlan.min_credits : qty) - 10)))}
                            style={{
                              width: "40px", height: "44px", borderRadius: "10px",
                              border: "1.5px solid #E2E8F0", background: "#F8FAFC",
                              fontSize: "20px", fontWeight: 700, color: "#475569",
                              cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                            }}
                          >−</button>
                          <input
                            type="number"
                            value={quantity}
                            min={titaniumPlan.min_credits}
                            onChange={(e) => handleQuantityChange(e.target.value)}
                            style={{
                              flex: 1, textAlign: "center",
                              padding: "10px 8px",
                              borderRadius: "10px",
                              border: quantityError ? "1.5px solid #FCA5A5" : "1.5px solid #E2E8F0",
                              background: "#F8FAFC",
                              fontSize: "16px", fontWeight: 800, color: "#0F172A",
                              outline: "none", height: "44px", boxSizing: "border-box",
                            }}
                          />
                          <button
                            onClick={() => handleQuantityChange(String(Math.min(titaniumPlan.max_credits, (isNaN(qty) ? titaniumPlan.min_credits : qty) + 10)))}
                            style={{
                              width: "40px", height: "44px", borderRadius: "10px",
                              border: "1.5px solid #E2E8F0", background: "#F8FAFC",
                              fontSize: "20px", fontWeight: 700, color: "#475569",
                              cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                            }}
                          >+</button>
                        </div>

                        {quantityError && (
                          <div style={{ display: "flex", alignItems: "center", gap: "5px", marginTop: "6px" }}>
                            <AlertCircle style={{ width: "12px", height: "12px", color: "#EF4444", flexShrink: 0 }} />
                            <span style={{ fontSize: "11px", color: "#DC2626", fontWeight: 500 }}>{quantityError}</span>
                          </div>
                        )}
                      </div>

                      {/* Pricing summary */}
                      {isQuantityValid && (
                        <div style={{
                          background: "#F8FAFC",
                          border: `1.5px solid ${isEnterprise ? "rgba(109,74,255,0.20)" : "#F1F5F9"}`,
                          borderRadius: "12px", padding: "14px 16px",
                          marginBottom: "14px",
                          display: "flex", flexDirection: "column", gap: "8px",
                        }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <span style={{ fontSize: "12px", color: "#94A3B8", fontWeight: 500 }}>Subtotal</span>
                            <div style={{ textAlign: "right" }}>
                              {discountPercent > 0 && (
                                <div style={{ fontSize: "10px", color: "#CBD5E1", textDecoration: "line-through" }}>
                                  {formatINR(originalTotal)}
                                </div>
                              )}
                              <span style={{ fontSize: "13px", fontWeight: 700, color: discountPercent > 0 ? "#059669" : "#0F172A" }}>
                                {formatINR(total)}
                              </span>
                            </div>
                          </div>

                          <div style={{
                            display: "flex", justifyContent: "space-between", alignItems: "center",
                            paddingTop: "6px", borderTop: "1px dashed #E2E8F0",
                          }}>
                            <span style={{ fontSize: "12px", color: "#94A3B8", fontWeight: 500 }}>GST (18%)</span>
                            <span style={{ fontSize: "12px", fontWeight: 600, color: "#64748B" }}>+ {formatINR(gstAmount)}</span>
                          </div>

                          <div style={{
                            display: "flex", justifyContent: "space-between", alignItems: "center",
                            paddingTop: "10px",
                            borderTop: `2px solid ${isEnterprise ? "#6D4AFF" : "#D4A017"}`,
                          }}>
                            <span style={{ fontSize: "12px", color: "#0F172A", fontWeight: 700 }}>Grand Total</span>
                            <span style={{ fontSize: "20px", fontWeight: 900, color: isEnterprise ? "#6D4AFF" : "#D4A017", letterSpacing: "-0.02em" }}>
                              {formatINR(totalWithGst)}
                            </span>
                          </div>

                          {collegeChallengeEnabled && discountPercent > 0 && !isEnterprise && (
                            <div style={{
                              paddingTop: "6px", borderTop: "1px solid #BBF7D0",
                              display: "flex", alignItems: "center", gap: "5px",
                              fontSize: "11px", color: "#065F46", fontWeight: 600,
                            }}>
                              <Check style={{ width: "11px", height: "11px", strokeWidth: 3, flexShrink: 0 }} />
                              You save {formatINR(originalTotal - total)}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Enterprise block — compact alert + collapsible info */}
                      {isQuantityValid && isEnterprise ? (
                        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                          {/* Compact alert */}
                          <div style={{
                            background: "rgba(109,74,255,0.05)",
                            border: "1.5px solid rgba(109,74,255,0.18)",
                            borderRadius: "12px", padding: "12px 14px",
                          }}>
                            <p style={{ fontSize: "12px", fontWeight: 700, color: "#4C1D95", margin: "0 0 2px" }}>
                              Enterprise Purchase
                            </p>
                            <p style={{ fontSize: "11px", color: "#6D4AFF", margin: "0 0 8px", fontWeight: 500 }}>
                              Online payment unavailable · Quotation valid 15 days
                            </p>
                            <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
                              {[
                                "GST Invoice Included",
                                "NEFT / RTGS Supported",
                                "Purchase Order Accepted",
                              ].map((item, i) => (
                                <div key={i} style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                  <Check style={{ width: "11px", height: "11px", color: "#6D4AFF", strokeWidth: 3, flexShrink: 0 }} />
                                  <span style={{ fontSize: "11px", color: "#5B21B6", fontWeight: 600 }}>{item}</span>
                                </div>
                              ))}
                            </div>
                          </div>

                          <button
                            onClick={handleEnterprisePurchase}
                            disabled={enterpriseLoading}
                            style={{
                              width: "100%", height: "56px",
                              borderRadius: "14px", border: "none",
                              background: enterpriseLoading ? "#94A3B8" : "linear-gradient(135deg, #6D4AFF, #4C1D95)",
                              color: "#FFFFFF",
                              fontSize: "15px", fontWeight: 800,
                              cursor: enterpriseLoading ? "not-allowed" : "pointer",
                              display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
                              boxShadow: enterpriseLoading ? "none" : "0 6px 20px rgba(109,74,255,0.40)",
                              letterSpacing: "0.01em",
                              transition: "opacity .15s",
                            }}
                            onMouseEnter={(e) => { if (!enterpriseLoading) e.currentTarget.style.opacity = "0.88"; }}
                            onMouseLeave={(e) => { e.currentTarget.style.opacity = "1"; }}
                          >
                            {enterpriseLoading ? (
                              <><RefreshCw style={{ width: "16px", height: "16px", animation: "spin 1s linear infinite" }} /> Generating...</>
                            ) : (
                              <><FileText style={{ width: "16px", height: "16px" }} /> Generate Quotation</>
                            )}
                          </button>
                        </div>
                      ) : (
                        /* Razorpay button */
                        <button
                          onClick={handlePurchase}
                          disabled={purchasing || !isQuantityValid}
                          style={{
                            width: "100%", height: "56px",
                            borderRadius: "14px", border: "none",
                            background: (purchasing || !isQuantityValid)
                              ? "#94A3B8"
                              : "linear-gradient(135deg, #6D4AFF, #9B72FF)",
                            color: "#FFFFFF",
                            fontSize: "15px", fontWeight: 800,
                            cursor: (purchasing || !isQuantityValid) ? "not-allowed" : "pointer",
                            display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
                            boxShadow: (purchasing || !isQuantityValid) ? "none" : "0 6px 20px rgba(109,74,255,0.35)",
                            letterSpacing: "0.01em",
                            transition: "opacity .15s",
                          }}
                          onMouseEnter={(e) => { if (!purchasing && isQuantityValid) e.currentTarget.style.opacity = "0.88"; }}
                          onMouseLeave={(e) => { e.currentTarget.style.opacity = "1"; }}
                        >
                          {purchasing ? (
                            <><RefreshCw style={{ width: "16px", height: "16px", animation: "spin 1s linear infinite" }} /> Processing...</>
                          ) : (
                            <>Purchase via Razorpay <ChevronRight style={{ width: "16px", height: "16px" }} /></>
                          )}
                        </button>
                      )}

                      {/* Footnote — pinned to bottom */}
                      <div style={{ marginTop: "auto", paddingTop: "12px" }}>
                        <p style={{ fontSize: "10px", color: "#CBD5E1", textAlign: "center", margin: 0, fontWeight: 500 }}>
                          Secure · GST invoice · 1-year subscription
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* College info footnote */}
                  {collegeInfo && (
                    <p style={{ textAlign: "center", fontSize: "11px", color: "#94A3B8", marginTop: "10px", fontWeight: 500 }}>
                      Max credits based on{" "}
                      <strong style={{ color: "#64748B" }}>
                        {collegeInfo.approved_student_count?.toLocaleString("en-IN") || "—"} approved students
                      </strong>
                    </p>
                  )}
                </div>
              </div>
            ) : (
              /* No plan available */
              <div style={{
                textAlign: "center", padding: "60px 24px",
                background: "#FFFFFF", borderRadius: "20px",
                border: "1.5px solid #E2E8F0",
              }}>
                <Shield style={{ width: "40px", height: "40px", color: "#D4A017", margin: "0 auto 16px" }} />
                <p style={{ fontSize: "16px", fontWeight: 700, color: "#0F172A", margin: "0 0 8px" }}>
                  Titanium Elite Plan Coming Soon
                </p>
                <p style={{ fontSize: "13px", color: "#64748B", fontWeight: 500 }}>
                  Contact support to enable the Titanium Elite plan for your institution.
                </p>
              </div>
            )}

            {/* ── TRUST INDICATORS ────────────────────────────────────────────── */}
            <div style={{
              borderTop: "1px solid #F1F5F9",
              paddingTop: "32px",
              marginTop: "40px",
              display: "flex",
              gap: "32px",
              flexWrap: "wrap",
              justifyContent: "center",
            }}>
              {[
                { icon: <TrendingUp style={{ width: "16px", height: "16px", color: "#6D4AFF" }} />, label: "Unlimited Scale", bg: "rgba(109,74,255,0.08)" },
                { icon: <CreditCard style={{ width: "16px", height: "16px", color: "#D4A017" }} />, label: "Secure Payments", bg: "rgba(212,160,23,0.10)" },
                { icon: <Clock style={{ width: "16px", height: "16px", color: "#059669" }} />, label: "Annual Subscription", bg: "rgba(5,150,105,0.08)" },
                { icon: <Shield style={{ width: "16px", height: "16px", color: "#6D4AFF" }} />, label: "GST Invoice Included", bg: "rgba(109,74,255,0.08)" },
              ].map((item, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <div style={{
                    width: "32px", height: "32px", borderRadius: "10px",
                    background: item.bg,
                    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                  }}>
                    {item.icon}
                  </div>
                  <span style={{ fontSize: "12px", fontWeight: 600, color: "#475569" }}>{item.label}</span>
                </div>
              ))}
            </div>

          </div>
        </main>
      </div>

      <CollegeFooter />

      <style>{`
        footer[aria-label*="college Portal footer"] {
           transition: margin-left 300ms ease;
        }
        @keyframes spin { to { transform: rotate(360deg); } }
        @media (max-width: 900px) {
          .credit-purchase-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
};

export default CollegeCreditPurchase;
